import type {
  ComponentProps,
  FocusEvent,
  ReactElement,
  ReactNode,
} from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  IconButton2,
  Text,
  TextField,
  theme,
  truncateStyles,
  useFocusVisible,
} from '@frameio/vapor';
import { IconCheckmark } from '@frameio/vapor-icons';
import { useRefBox } from '@frameio/vapor-migration/hooks/useRefBox';
import styles from './CopyLink.module.css';
import TEXT from './generated/TEXT/CopyLink';

TEXT.DEFINE({
  /** Label for copy link button */
  label: 'Copy link',
  /** Success message when link is copied */
  linkCopied: 'Link copied!',
});

const COPY_LINK_EVENT_PREFIX = 'copy-link';
const COPIED_FEEDBACK_DURATION_MS = 2000;

type CopyLinkEventDetail = { url: string };

/**
 * Event name for a copy-link group. Copy links sharing a `linkGroupId` listen on
 * the same channel, so a copy of a given url shows the confirmation on all of
 * them at once.
 */
export function getCopyLinkEventName(linkGroupId: string) {
  return `${COPY_LINK_EVENT_PREFIX}:${linkGroupId}`;
}

/**
 * Announces that `url` was copied within `linkGroupId`. Feature copy-actions
 * call this so any mounted `CopyLink` in the same group shows its confirmation,
 * even when the copy originated somewhere other than the field itself.
 */
export function dispatchCopyLinkEvent(linkGroupId: string, url: string) {
  window.dispatchEvent(
    new CustomEvent<CopyLinkEventDetail>(getCopyLinkEventName(linkGroupId), {
      detail: { url },
    })
  );
}

export type CopyLinkProps = {
  'data-dd-action-name'?: string;
  adornmentAfter?: ReactNode;
  disabled?: boolean;
  /**
   * Show only the checkmark (no label text) in the copied confirmation. Use in
   * narrow fields where the label would overlap the url.
   */
  iconOnlyConfirmation?: boolean;
  label?: string;
  /** Text shown in the confirmation feedback after a copy. */
  linkCopied?: string;
  /**
   * Groups copy links onto a shared event channel. Instances with the same
   * `linkGroupId` show the copied confirmation together whenever a matching url
   * is copied — either by clicking one of them, or when feature code announces a
   * copy via `dispatchCopyLinkEvent`.
   */
  linkGroupId?: string;
  /** Performs the actual copy when the field is clicked. */
  onCopy?: (url: string) => void | Promise<void>;
  url: string;
} & Pick<ComponentProps<typeof TextField>, 'size'>;

export function CopyLink({
  'data-dd-action-name': ddActionName,
  adornmentAfter = null,
  disabled = false,
  iconOnlyConfirmation = false,
  label = TEXT.label,
  linkCopied = TEXT.linkCopied,
  linkGroupId,
  size = 'medium',
  onCopy,
  url,
}: CopyLinkProps) {
  const [wasCopied, setWasCopied] = useState(false);

  const isDisabled = disabled || !url || url.length === 0;

  const showCopiedConfirmation = useCallback(() => {
    setWasCopied(true);
  }, []);

  const box = useRefBox({
    input: useRef<HTMLInputElement>(null),
    onCopy,
    showCopiedConfirmation,
    url,
  });

  const copyLink = useCallback(async () => {
    const { current } = box;
    await current.onCopy?.(current.url);
    current.showCopiedConfirmation();
  }, [box]);

  const handleInputClick = useCallback(async () => {
    box.current.input.current?.select?.();
    await copyLink();
  }, [box, copyLink]);

  const handleButtonClick = useCallback(async () => {
    await copyLink();
  }, [copyLink]);

  useEffect(() => {
    if (!linkGroupId) {
      return;
    }

    const eventName = getCopyLinkEventName(linkGroupId);

    function handleCopied(event: CustomEvent<CopyLinkEventDetail>) {
      if (event.detail.url === box.current.url) {
        box.current.showCopiedConfirmation();
      }
    }

    window.addEventListener(eventName, handleCopied as EventListener);
    // eslint-disable-next-line consistent-return
    return () =>
      window.removeEventListener(eventName, handleCopied as EventListener);
  }, [box, linkGroupId]);

  // Pausing while focus is anywhere in the group (input or button) keeps the
  // confirmation visible while a keyboard user is still interacting with it.
  const { isFocusVisible } = useFocusVisible();
  const [isControlFocused, setIsControlFocused] = useState(false);

  useEffect(() => {
    if (!wasCopied || isControlFocused) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setWasCopied(false);
    }, COPIED_FEEDBACK_DURATION_MS);

    return () => clearTimeout(timeout);
  }, [wasCopied, isControlFocused]);

  const handleGroupFocus = useCallback(() => {
    if (isFocusVisible) {
      setIsControlFocused(true);
    }
  }, [isFocusVisible]);

  const handleGroupBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsControlFocused(false);
    }
  }, []);

  return (
    <div
      onBlur={handleGroupBlur}
      onFocus={handleGroupFocus}
      style={{ display: 'contents' }}
    >
      <StyledTextField
        ref={box.current.input}
        adornmentAfter={
          adornmentAfter && (
            <>
              <div className={styles.copyLinkCopied} data-copied={wasCopied}>
                {!iconOnlyConfirmation && (
                  <Text color="success" variant="linkSmall">
                    {linkCopied}
                  </Text>
                )}
                <IconCheckmark color="success" size={16} />
              </div>
              <IconButton2
                icon={adornmentAfter as ReactElement<any>}
                isDisabled={isDisabled}
                label={label}
                onClick={handleButtonClick}
                size="small"
                variant="dimmed"
                {...{
                  className: styles.copyLinkIcon,
                  'data-copied': wasCopied,
                  ...(ddActionName
                    ? { 'data-dd-action-name': ddActionName }
                    : {}),
                }}
              />
            </>
          )
        }
        aria-label={label}
        className="copy-link-input"
        disabled={isDisabled}
        onClick={handleInputClick}
        placeholder={url}
        readOnly={true}
        size={size}
        value={!isDisabled ? url : ''}
      />
    </div>
  );
}

const StyledTextField = styled(TextField)`
  input {
    border-radius: inherit;
    cursor: pointer;
    ${truncateStyles};

    &:focus-visible {
      outline: 1.5px solid ${theme.colors.border.interactive.focused};
      outline-offset: 1.5px;
    }
  }

  &[data-disabled] {
    input {
      user-select: none;
    }

    input::placeholder {
      color: ${theme.colors.text.primary};
    }
  }
`;
